import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Plus16 } from '@makyoapp/makyo-icon/icon'
import { Button, Title } from '@makyoapp/makyo-ui'

import { useOrgsContext } from '@/context/OrgsContext'
import useAuth from '@/hooks/useAuth'
import { WrapperProps } from '@/shared/routes'
import Layout from '@/components/Layout'
import { ListOrgCard } from '@/components/ListOrgCard'
import { CardSkeletons } from '@/components/Skeletons'

/**
 * MyOrganizations component is a page for managing user's organizations.
 *
 * @route `/u/my-orgs`
 */
export default function MyOrganizations(props: WrapperProps) {
  const { t } = useTranslation()
  const { userData } = useAuth()
  const { isOrgsLoading, orgs, updateOrgs, setShowCreateOrg, orgsPerms, updateOrgsPerms } = useOrgsContext()

  const isLoading = isOrgsLoading

  // ============================ Orgs Groups ============================

  const myOrgs = useMemo(
    () =>
      orgs
        .filter((go) => go.ownerId === userData?.id)
        .sort((goa, gob) => {
          return gob.updated.getTime() - goa.updated.getTime()
        }),
    [orgs, userData?.id]
  )

  const otherOrgs = useMemo(
    () =>
      orgs
        .filter((go) => go.ownerId !== userData?.id)
        .sort((goa, gob) => {
          return gob.updated.getTime() - goa.updated.getTime()
        }),
    [orgs, userData?.id]
  )

  // ============================ Orgs Perms specifig ============================

  const handleList = useCallback(async () => {
    const newOrgs = await updateOrgs()
    await updateOrgsPerms(newOrgs)
  }, [updateOrgs, updateOrgsPerms])

  // check if orgsPerms length are different from orgs length
  const shouldLoadOrgsPerms = useMemo(() => {
    return orgs && Object.keys(orgsPerms).length !== orgs.length
  }, [orgs, orgsPerms])

  /**
   * update org perms when user visit this page
   */
  useEffect(() => {
    if (shouldLoadOrgsPerms) {
      updateOrgsPerms(orgs)
    }
  }, [shouldLoadOrgsPerms, updateOrgsPerms, orgs])

  // ============================ Render ============================

  return (
    <Layout
      title={props.title}
      actions={
        <>
          <Button
            id='new-org-btn'
            icon={<Plus16 />}
            onClick={() => {
              setShowCreateOrg(true)
            }}
          >
            {t('Add New Organization')}
          </Button>
        </>
      }
    >
      <div className='flex flex-col gap-12'>
        <>
          <div>
            <Title gap='short'>{t('Organizations I own')}</Title>
            {isLoading ? <CardSkeletons total={3} /> : <ListOrgCard orgs={myOrgs} onAfterUpdate={handleList} />}
          </div>
          {/* <hr className='bdr' /> */}
          <div>
            <Title gap='short'>{t('Organizations I belong to')}</Title>
            {isLoading ? (
              <CardSkeletons total={3} />
            ) : (
              <ListOrgCard id='my-org' orgs={otherOrgs} onAfterUpdate={handleList} />
            )}
          </div>
        </>
      </div>
    </Layout>
  )
}
